/**
 * Visual effects — steam bursts, spark showers, score fly-ups,
 * personal best flash. Steampunk clock tower theme.
 * All effects respect prefers-reduced-motion.
 */

import { Container, Graphics, Sprite, Text, TextStyle, type Texture } from 'pixi.js';
import type { Vec2 } from '../game/types.js';
import type { ScoreBreakdown } from '../game/scoring/score.js';
import type { SpritePool } from './sprite-pool.js';
import { createGearTexture } from './sprite-pool.js';
import { CONFIG } from '../game/config.js';
import { drawGearGraphic } from './gear-graphic.js';
import { bubbleGearRotation } from './board-renderer.js';
import { PRISMATIC_HEAT_MS } from './prismatic-timing.js';
import {
  POP_SPIN_MS,
  CASCADE_ARC_STAGGER_MS,
  CASCADE_ARC_GROW_MS,
  CASCADE_ARC_HOLD_MS,
} from './cascade-timing.js';

export { CASCADE_ARC_GROW_MS, cascadeArcArrivalMs, cascadeArcStaggerMs } from './cascade-timing.js';

/**
 * Lazy-initialised gear texture shared with the sprite pool's own texture
 * (same `createGearTexture(CONFIG.CELL_SIZE)` call). Effects that need to
 * render a "still on the board" gear during a delay spawn a `Sprite` from
 * this — pixel-identical to a board bubble, so the transition from board
 * sprite → held sprite is invisible on the wire. `Graphics`-drawn gears
 * look subtly heavier/rougher and read as "an ugly, chunky change" on
 * video.
 *
 * `document` may not exist under SSR/tests; when it doesn't we fall back
 * to `null` and callers use `drawGearGraphic` instead.
 */
let sharedGearTexture: Texture | null = null;
function getGearTexture(): Texture | null {
  if (sharedGearTexture) return sharedGearTexture;
  if (typeof document === 'undefined') return null;
  sharedGearTexture = createGearTexture(CONFIG.CELL_SIZE);
  return sharedGearTexture;
}

/**
 * Symbol style for the engraved colourblind marker on top of a held gear.
 * Matches board-renderer.ts:SYMBOL_STYLE so the held stand-in draws with
 * the exact same overlay the board sprite had.
 */
const HELD_SYMBOL_STYLE = new TextStyle({
  fontFamily: '"Courier New", monospace',
  fontSize: 10,
  fill: 0x1a1510,
  align: 'center',
});

/**
 * A held stand-in for a board gear that has been logically removed but is
 * being visually held in place until an effect (a cascade arc, a delayed
 * drop) resolves. `node` bundles the gear sprite + engraved symbol into a
 * single `Container` so animating position / scale / alpha moves both
 * together. Callers should also spin `sprite.rotation` for the pop
 * animation — the symbol intentionally stays upright, matching how the
 * board renders it (board sprites rotate, symbol text does not).
 */
interface HeldGear {
  readonly node: Container;
  readonly sprite: Sprite;
}

/**
 * Build a held gear that is pixel-identical to the board sprite at `key`
 * (same texture, same scale, same per-cell rotation, same engraved
 * symbol). When the board sprite + symbol text are released and this
 * replaces them there is no layout shift, no rotation snap, no colour
 * change, and no missing engraving.
 *
 * Returns `null` under SSR/tests (no `document`), so callers fall through
 * to the `Graphics` path.
 */
function createHeldBoardGear(color: number, symbol: string, key: string): HeldGear | null {
  const texture = getGearTexture();
  if (!texture) return null;

  const node = new Container();

  const sprite = new Sprite(texture);
  sprite.anchor.set(0.5);
  sprite.tint = color;
  // Board-renderer.ts:renderBoard scales its pool sprites to CELL_SIZE:
  //   sprite.scale.set(cellSize / (sprite.texture.width / 2))
  sprite.scale.set(CONFIG.CELL_SIZE / (texture.width / 2));
  sprite.rotation = bubbleGearRotation(key);
  node.addChild(sprite);

  if (symbol.length > 0) {
    const text = new Text({ text: symbol, style: HELD_SYMBOL_STYLE });
    text.anchor.set(0.5);
    node.addChild(text);
  }

  return { node, sprite };
}

const POP_PARTICLE_COUNT = 8;
const POP_DURATION_MS = 400;
const DROP_MAX_MS = 3000;
const GRAVITY = 0.35;
const FLY_UP_DURATION_MS = 800;
const FLY_UP_DISTANCE = 60;
const EMBER_DURATION_MS = 400;
const EMBER_COLORS = [0xff8c3a, 0xffb060, 0xffe8a0];
const FRENZY_AMBER = 0xff8c3a;
const CASCADE_ARC_FADE_MS = 200;

const PRISMATIC_SHARD_COUNT = 8;
const PRISMATIC_SHARD_MS = 500;
const PRISMATIC_COLORS = [
  0xd03030, 0xd48828, 0xe8c840, 0x6b8f4a, 0x4a8fa8, 0x8b6aad,
];

interface ActiveEffect {
  update(elapsed: number): boolean; // returns true when complete
  destroy(): void;
}

/** Global list of active effects, ticked by the game renderer */
export const activeEffects: ActiveEffect[] = [];

/**
 * Tick all active effects. Call once per frame.
 * @param dt - delta time in milliseconds
 */
export function updateEffects(dt: number): void {
  for (let i = activeEffects.length - 1; i >= 0; i--) {
    const effect = activeEffects[i]!;
    if (effect.update(dt)) {
      effect.destroy();
      activeEffects.splice(i, 1);
    }
  }
}

/**
 * Drop every active effect. Call when the renderer that owns their
 * containers is being destroyed — otherwise the module-level
 * `activeEffects` array survives GameScreen unmount/remount (e.g. on
 * Ascend Again) and the next frame ticks stale effects whose gfx have
 * already been destroyed with their parent container, crashing on
 * `g.gfx.position.set(...)` with "Cannot read properties of null".
 */
export function clearActiveEffects(): void {
  for (const effect of activeEffects) {
    try {
      effect.destroy();
    } catch {
      // gfx may already be destroyed by its parent container — ignore.
    }
  }
  activeEffects.length = 0;
}

/**
 * Create a spin-shrink effect for matched gears before they disappear.
 * The gear spins with rapid acceleration while shrinking to nothing over 250ms.
 * After completion, triggers the steam/spark particle burst.
 */
export function createPopSpinEffect(
  container: Container,
  position: Vec2,
  color: number,
  pool: SpritePool | null,
  reducedMotion: boolean,
): void {
  if (reducedMotion) {
    // Skip spin, go straight to particles
    createPopEffect(container, position, color, pool, reducedMotion);
    return;
  }

  const GEAR_R = 10;
  const TEETH = 8;

  const gfx = new Graphics();
  // Draw gear shape matching the board gears
  const outerR = GEAR_R;
  const innerR = GEAR_R * 0.75;
  const half = Math.PI / (TEETH * 2.5);
  gfx.moveTo(outerR, 0);
  for (let i = 0; i < TEETH; i++) {
    const b = (i / TEETH) * Math.PI * 2;
    const n = ((i + 1) / TEETH) * Math.PI * 2;
    const a2 = b + half;
    const a4 = n - half;
    gfx.lineTo(Math.cos(a2) * outerR, Math.sin(a2) * outerR);
    gfx.lineTo(Math.cos(a2) * innerR, Math.sin(a2) * innerR);
    gfx.lineTo(Math.cos(a4) * innerR, Math.sin(a4) * innerR);
    gfx.lineTo(Math.cos(a4) * outerR, Math.sin(a4) * outerR);
  }
  gfx.closePath();
  gfx.fill({ color });
  gfx.circle(0, 0, GEAR_R * 0.2);
  gfx.fill({ color: 0x1a1510 });

  // Position at the pop location; pivot at local center so scale/rotation
  // operate around the gear's center, not the top-left bounds corner.
  gfx.pivot.set(0, 0);
  gfx.position.set(position.x, position.y);
  container.addChild(gfx);

  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / POP_SPIN_MS;
      if (t >= 1) {
        // Spin done — trigger particles
        createPopEffect(container, position, color, pool, reducedMotion);
        return true;
      }

      // Accelerating spin: starts slow, ends fast (t² curve)
      gfx.rotation = t * t * Math.PI * 6;

      // Shrink to nothing — keep position locked at center
      const scale = Math.max(0.01, 1 - t);
      gfx.scale.set(scale);
      gfx.position.set(position.x, position.y);

      // Slight fade at the very end
      gfx.alpha = t > 0.8 ? 1 - (t - 0.8) / 0.2 : 1;

      return false;
    },
    destroy(): void {
      container.removeChild(gfx);
      gfx.destroy();
    },
  });
}

/** Hard cap on the thrown gear's flight, in case bounds are degenerate. */
const STEAL_THROW_MAX_MS = 4000;
const STEAL_TRAIL_LEN = 7;
const STEAL_TRAIL_SAMPLE_MS = 28;

/** Visible-scene rectangle (stage-local px) the thrown gear must fully exit. */
export interface ThrowBounds {
  readonly left: number;
  readonly right: number;
  readonly bottom: number;
}

export interface StealThrowOptions {
  /** Rotation the gear had in the Rogue's hands, for a seamless hand-off. */
  readonly rotation?: number;
  /** Scene edges; the gear stays alive until it has left them entirely. */
  readonly bounds?: ThrowBounds;
  /** Gear radius — should match the board gear so it reads as the same object. */
  readonly radius?: number;
}

/**
 * Cascade-Coil pop: places a board-identical gear Sprite at `position`,
 * holds it there until `arrivalMs` (when the lightning arc actually
 * lands), then spins + shrinks it to nothing over POP_SPIN_MS and
 * finishes with a steam-burst. Because the sprite shares the pool's
 * texture, the held phase is visually indistinguishable from the board
 * gear it replaces — no "chunky redraw" the moment the strike starts.
 *
 * Frenzy embers spawn at arrival, matching non-cascade pop behavior.
 */
export function createCascadeGearPopEffect(
  container: Container,
  position: Vec2,
  color: number,
  symbol: string,
  key: string,
  arrivalMs: number,
  reducedMotion: boolean,
  frenzyActive: boolean,
): void {
  if (reducedMotion || arrivalMs <= 0) {
    createPopSpinEffect(container, position, color, null, reducedMotion);
    if (frenzyActive && !reducedMotion) {
      createEmberParticles(container, position, reducedMotion);
    }
    return;
  }

  const held = createHeldBoardGear(color, symbol, key);
  const gearNode: Container | Graphics = held?.node ?? (() => {
    const g = new Graphics();
    drawGearGraphic(g, CONFIG.CELL_SIZE, color);
    return g;
  })();
  const spinner: { rotation: number } = held ? held.sprite : gearNode;
  const baseSpinnerRotation = spinner.rotation;
  gearNode.position.set(position.x, position.y);
  container.addChild(gearNode);

  let elapsed = 0;
  let particlesSpawned = false;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      if (elapsed < arrivalMs) {
        // Held phase — pixel-identical to a board bubble at this cell.
        return false;
      }
      const spinT = (elapsed - arrivalMs) / POP_SPIN_MS;
      if (spinT >= 1) {
        if (!particlesSpawned) {
          particlesSpawned = true;
          createPopEffect(container, position, color, null, false);
          if (frenzyActive) createEmberParticles(container, position, false);
        }
        return true;
      }
      // Sprite carries the per-cell rotation (board's rotation is on the
      // sprite, not the container); spin on top of that. The symbol text
      // stays upright — matches how the board renders it.
      spinner.rotation = baseSpinnerRotation + spinT * spinT * Math.PI * 6;
      gearNode.scale.set(Math.max(0.01, 1 - spinT));
      gearNode.alpha = spinT > 0.8 ? 1 - (spinT - 0.8) / 0.2 : 1;
      return false;
    },
    destroy(): void {
      if (gearNode.parent) container.removeChild(gearNode);
      gearNode.destroy({ children: true });
    },
  });
}

/**
 * Cascade-Coil drop: for gears the coil strike LEAVES UNSUPPORTED (they
 * survive electrification but their supports get vaporised), hold a
 * board-identical gear at `position` until `delayMs` — the moment the
 * last electrified support pops — and only then release it into the
 * standard flying-drop physics. Preserves causality: unsupported gears
 * don't fall until the gears holding them up have been destroyed.
 *
 * Under reduced-motion or a zero delay this degrades to the normal
 * `createDropEffect` for a single position, which the caller batches
 * with same-colour drops as usual.
 */
export function createCascadeDropEffect(
  container: Container,
  position: Vec2,
  color: number,
  symbol: string,
  key: string,
  delayMs: number,
  reducedMotion: boolean,
): void {
  if (reducedMotion) return;
  if (delayMs <= 0) {
    createDropEffect(container, [position], color, reducedMotion);
    return;
  }

  const held = createHeldBoardGear(color, symbol, key);
  const gearNode: Container | Graphics = held?.node ?? (() => {
    const g = new Graphics();
    drawGearGraphic(g, CONFIG.CELL_SIZE, color);
    return g;
  })();
  gearNode.position.set(position.x, position.y);
  container.addChild(gearNode);

  let elapsed = 0;
  let released = false;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      if (elapsed < delayMs) return false;
      if (!released) {
        released = true;
        createDropEffect(container, [position], color, false);
      }
      return true;
    },
    destroy(): void {
      if (gearNode.parent) container.removeChild(gearNode);
      gearNode.destroy({ children: true });
    },
  });
}


/**
 * Rogue theft visual — the stolen gear leaves the Rogue's hands and sails
 * off-screen in a long downward arc. The pry itself is animated on the board
 * gear + Rogue sprite; this picks up at the moment of release.
 *
 * The gear is never faded or culled early: it stays fully opaque until it has
 * physically left the visible scene (side margin or bottom edge), so on wide
 * viewports the player watches it fly clean through the margin outside the
 * play area.
 *
 * `throwDir` is -1 to arc toward the left edge, +1 toward the right.
 */
export function createRogueStealThrowEffect(
  container: Container,
  position: Vec2,
  color: number,
  throwDir: number,
  reducedMotion: boolean,
  options: StealThrowOptions = {},
): void {
  const radius = options.radius ?? CONFIG.CELL_SIZE;
  const bounds = options.bounds ?? {
    left: 0,
    right: CONFIG.BOARD_WIDTH_PX,
    bottom: CONFIG.BOARD_HEIGHT_PX,
  };

  // Trail sits under the gear so the streak never occludes the silhouette.
  const trail = new Graphics();
  const gfx = new Graphics();
  drawGearGraphic(gfx, radius, color);
  gfx.position.set(position.x, position.y);
  gfx.rotation = options.rotation ?? 0;
  container.addChild(trail);
  container.addChild(gfx);

  const destroy = (): void => {
    container.removeChild(trail);
    container.removeChild(gfx);
    trail.destroy();
    gfx.destroy();
  };

  if (reducedMotion) {
    const FADE_MS = 280;
    let elapsed = 0;
    activeEffects.push({
      update(dt: number): boolean {
        elapsed += dt;
        const t = elapsed / FADE_MS;
        if (t >= 1) return true;
        gfx.alpha = 1 - t;
        gfx.scale.set(Math.max(0.2, 1 - t * 0.8));
        return false;
      },
      destroy,
    });
    return;
  }

  const dir = throwDir < 0 ? -1 : 1;
  // A short rise then a long fall: the apex comes ~0.3s in, so most of the
  // flight reads as a downward arc. Horizontal speed is high enough to clear
  // the board and carry on through the side margin before the gear drops out
  // of the bottom of the scene.
  // On a narrow scene (phone: no side margins) full speed would carry it off
  // the edge while it's still near the apex, so slow it enough that the fall
  // is on screen too.
  const runway = dir > 0 ? bounds.right - position.x : position.x - bounds.left;
  const VX = Math.max(240, Math.min(430, runway / 0.8)) * dir;
  const VY = -330;
  const G = 1000;
  const startRot = gfx.rotation;
  const margin = radius * 2;

  let elapsed = 0;
  let sinceSample = 0;
  const history: Vec2[] = [];

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const tSec = elapsed / 1000;
      const x = position.x + VX * tSec;
      const y = position.y + VY * tSec + 0.5 * G * tSec * tSec;

      const gone =
        x < bounds.left - margin ||
        x > bounds.right + margin ||
        y > bounds.bottom + margin;
      if (gone || elapsed >= STEAL_THROW_MAX_MS) return true;

      sinceSample += dt;
      if (sinceSample >= STEAL_TRAIL_SAMPLE_MS) {
        sinceSample = 0;
        history.push({ x: gfx.position.x, y: gfx.position.y });
        if (history.length > STEAL_TRAIL_LEN) history.shift();
      }

      gfx.position.set(x, y);
      gfx.rotation = startRot + dir * tSec * 11;
      // Slight swell sells the gear being hurled out of the tower, toward us.
      gfx.scale.set(1 + Math.min(0.2, tSec * 0.25));

      trail.clear();
      for (let i = 0; i < history.length; i++) {
        const f = (i + 1) / (history.length + 1);
        trail.circle(history[i]!.x, history[i]!.y, radius * (0.35 + 0.45 * f));
        trail.fill({ color, alpha: 0.28 * f });
      }
      return false;
    },
    destroy,
  });
}

/**
 * Rivet-spark burst — short radial burst of warm-amber sparks fired from a
 * Close Match 2-cluster landing site, before the pop animation plays.
 * 5 particles, ~150ms.
 */
export function createRivetSparkBurst(
  container: Container,
  position: Vec2,
  reducedMotion: boolean,
): void {
  if (reducedMotion) return;

  const COUNT = 5;
  const DURATION_MS = 150;
  const particles: { gfx: Graphics; vx: number; vy: number }[] = [];

  for (let i = 0; i < COUNT; i++) {
    const angle = (i / COUNT) * Math.PI * 2 + Math.random() * 0.3;
    const speed = 2.2 + Math.random() * 0.8;
    const gfx = new Graphics();
    gfx.rect(-1, -1, 2, 2);
    gfx.fill({ color: 0xd4a539 });
    gfx.position.set(position.x, position.y);
    container.addChild(gfx);
    particles.push({
      gfx,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
    });
  }

  let elapsed = 0;
  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / DURATION_MS;
      if (t >= 1) return true;
      for (const p of particles) {
        p.gfx.position.x += p.vx;
        p.gfx.position.y += p.vy;
        p.gfx.alpha = 1 - t;
      }
      return false;
    },
    destroy(): void {
      for (const p of particles) {
        container.removeChild(p.gfx);
        p.gfx.destroy();
      }
    },
  });
}

/**
 * Create a steam burst + spark effect when a gear-bubble pops.
 * Half the particles are white steam puffs, half are colored sparks.
 */
export function createPopEffect(
  container: Container,
  position: Vec2,
  color: number,
  pool: SpritePool | null,
  reducedMotion: boolean,
): void {
  if (reducedMotion) return;

  const particles: { gfx: Graphics; vx: number; vy: number; isSteam: boolean }[] = [];

  for (let i = 0; i < POP_PARTICLE_COUNT; i++) {
    const angle = (i / POP_PARTICLE_COUNT) * Math.PI * 2;
    const isSteam = i % 2 === 0;
    const speed = isSteam ? 1.0 + Math.random() * 0.5 : 1.5 + (i % 3) * 0.5;
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed + (isSteam ? -0.8 : 0); // steam rises

    const gfx = new Graphics();
    if (isSteam) {
      // Steam puff — white/grey circle
      gfx.circle(0, 0, 2 + Math.random() * 2);
      gfx.fill({ color: 0xe8dcc8 });
    } else {
      // Spark — tiny colored rectangle
      gfx.rect(-1, -1, 2, 3);
      gfx.fill({ color });
    }
    gfx.position.set(position.x, position.y);
    container.addChild(gfx);

    particles.push({ gfx, vx, vy, isSteam });
  }

  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / POP_DURATION_MS;
      if (t >= 1) return true;

      for (const p of particles) {
        p.gfx.position.x += p.vx;
        p.gfx.position.y += p.vy;
        if (p.isSteam) {
          // Steam expands and fades
          p.gfx.alpha = 1 - t;
          p.gfx.scale.set(1 + t * 0.8);
        } else {
          // Sparks shrink and dim
          p.gfx.alpha = 1 - t;
          p.gfx.scale.set(1 - t * 0.5);
        }
      }
      return false;
    },
    destroy(): void {
      for (const p of particles) {
        container.removeChild(p.gfx);
        p.gfx.destroy();
      }
    },
  });
}

/**
 * Spawn 3-5 upward-drifting ember particles that fade over 400ms.
 * Used during Score Frenzy to accent each popped cluster.
 */
export function createEmberParticles(
  container: Container,
  position: Vec2,
  reducedMotion: boolean,
): void {
  if (reducedMotion) return;

  const count = 3 + Math.floor(Math.random() * 3);
  interface Ember {
    gfx: Graphics;
    vx: number;
    vy: number;
    life: number;
  }
  const embers: Ember[] = [];

  for (let i = 0; i < count; i++) {
    const gfx = new Graphics();
    const size = 1.5 + Math.random() * 1.5;
    const color = EMBER_COLORS[i % EMBER_COLORS.length]!;
    gfx.circle(0, 0, size);
    gfx.fill({ color });
    gfx.position.set(
      position.x + (Math.random() - 0.5) * 8,
      position.y + (Math.random() - 0.5) * 4,
    );
    container.addChild(gfx);

    embers.push({
      gfx,
      vx: (Math.random() - 0.5) * 0.6,
      vy: -0.9 - Math.random() * 0.7,
      life: 1,
    });
  }

  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / EMBER_DURATION_MS;
      if (t >= 1) return true;

      for (const e of embers) {
        e.gfx.position.x += e.vx * (dt / 16);
        e.gfx.position.y += e.vy * (dt / 16);
        e.vy += 0.02 * (dt / 16);
        e.gfx.alpha = 1 - t;
      }
      return false;
    },
    destroy(): void {
      for (const e of embers) {
        container.removeChild(e.gfx);
        e.gfx.destroy();
      }
    },
  });
}

/**
 * Lightning-arc VFX for Cascade Coil. Draws jagged polylines from the landed
 * shot outward to each stripped gear. Each arc has three phases so the strike
 * reads as emanating from the landing point rather than snapping into place:
 *   - GROW: the polyline extends from `from` toward `to` (partial draw)
 *   - HOLD: brief full-alpha hold once the tip reaches the target
 *   - FADE: alpha ramps to 0
 * Arcs are sorted by distance from the landing point so the strike radiates
 * outward on the wire.
 */
export function createCascadeArcEffect(
  container: Container,
  arcs: ReadonlyArray<{ from: Vec2; to: Vec2 }>,
  color: number,
  reducedMotion: boolean,
  staggerMs: number = CASCADE_ARC_STAGGER_MS,
): void {
  if (reducedMotion || arcs.length === 0) return;

  const sorted = [...arcs].sort((a, b) => {
    const da = (a.to.x - a.from.x) ** 2 + (a.to.y - a.from.y) ** 2;
    const db = (b.to.x - b.from.x) ** 2 + (b.to.y - b.from.y) ** 2;
    return da - db;
  });

  const perArcMs = CASCADE_ARC_GROW_MS + CASCADE_ARC_HOLD_MS + CASCADE_ARC_FADE_MS;

  // Every arc in one strike shares its origin. The origin burst mimics the
  // in-flight cascade orb visually — dark storm core + cyan rim + expanding
  // cyan halo — so the strike reads as "the orb detonates" rather than
  // "amber circle with unrelated stuff inside". Tuned tight (small radius,
  // fast fade) so it doesn't visually crowd nearby held gears.
  const origin = sorted[0]!.from;
  const flashGfx = new Graphics();
  container.addChild(flashGfx);
  const FLASH_MS = 260;
  const CORE_R = CONFIG.CELL_SIZE * 0.7;

  const entries = sorted.map((arc, i) => {
    const dx = arc.to.x - arc.from.x;
    const dy = arc.to.y - arc.from.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = -dy / len;
    const ny = dx / len;
    // More jagged than before — 10 vertices with sharper offsets read as
    // a real bolt of lightning at HD, not a lazy squiggle.
    const steps = 10;
    const points: Vec2[] = [];
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      const jitter = s === 0 || s === steps ? 0 : (Math.random() - 0.5) * len * 0.22;
      points.push({
        x: arc.from.x + dx * t + nx * jitter,
        y: arc.from.y + dy * t + ny * jitter,
      });
    }
    const gfx = new Graphics();
    container.addChild(gfx);
    return { gfx, points, delay: i * staggerMs };
  });

  let elapsed = 0;
  const totalMs = (sorted.length - 1) * staggerMs + perArcMs;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;

      // Origin burst — expanding cyan shockwave, dark storm-cell core that
      // fades as the strike takes off, and a hot white pinprick that dies
      // quickly. Matches the flight orb's colour language so "coil arrives
      // → coil detonates" is one continuous read. Max radius stays close to
      // the orb's own size so nearby held gears are never inside it.
      flashGfx.clear();
      if (elapsed < FLASH_MS) {
        const ft = elapsed / FLASH_MS;
        const ringR = CORE_R + ft * CORE_R * 1.4;
        // Expanding cyan halo — the shockwave.
        flashGfx.circle(origin.x, origin.y, ringR);
        flashGfx.stroke({ color: 0x6fd4e8, width: 3, alpha: 0.85 * (1 - ft) });
        flashGfx.circle(origin.x, origin.y, ringR * 0.85);
        flashGfx.stroke({ color: 0xe8f8ff, width: 1.5, alpha: 0.9 * (1 - ft) });
        // Dark storm-cell core — fades to nothing over the first ~60% of
        // the burst so the arcs appear to *originate* from the coil
        // dissolving, not from a bright disc.
        const coreShrink = Math.max(0, 1 - ft * 1.6);
        if (coreShrink > 0) {
          flashGfx.circle(origin.x, origin.y, CORE_R * coreShrink);
          flashGfx.fill({ color: 0x1a2028, alpha: coreShrink });
          flashGfx.circle(origin.x, origin.y, CORE_R * coreShrink);
          flashGfx.stroke({ color: 0x6fd4e8, width: 1.3, alpha: coreShrink });
        }
        // Hot pinprick at the very centre — the coil's dying spark.
        const sparkR = CORE_R * 0.35 * Math.max(0, 1 - ft * 2.5);
        if (sparkR > 0) {
          flashGfx.circle(origin.x, origin.y, sparkR);
          flashGfx.fill({ color: 0xe8f8ff, alpha: 0.95 });
        }
      }

      for (const e of entries) {
        const local = elapsed - e.delay;
        if (local < 0) {
          e.gfx.alpha = 0;
          continue;
        }
        e.gfx.alpha = 1;
        e.gfx.clear();
        const pts = e.points;
        // Growth: how much of the polyline has the strike reached?
        // Runs 0 → 1 during GROW_MS, stays at 1 through HOLD_MS + FADE_MS.
        const growProgress = Math.min(local / CASCADE_ARC_GROW_MS, 1);
        const totalSegments = pts.length - 1;
        const drawnFloat = growProgress * totalSegments;
        const fullSegments = Math.min(Math.floor(drawnFloat), totalSegments - 1);
        const partial: Vec2 | null = growProgress < 1
          ? (() => {
              const frac = drawnFloat - fullSegments;
              const a = pts[fullSegments]!;
              const b = pts[fullSegments + 1]!;
              return { x: a.x + (b.x - a.x) * frac, y: a.y + (b.y - a.y) * frac };
            })()
          : null;

        const buildPath = (): void => {
          e.gfx.moveTo(pts[0]!.x, pts[0]!.y);
          for (let i = 1; i <= fullSegments; i++) {
            e.gfx.lineTo(pts[i]!.x, pts[i]!.y);
          }
          if (partial) {
            e.gfx.lineTo(partial.x, partial.y);
          } else {
            e.gfx.lineTo(pts[totalSegments]!.x, pts[totalSegments]!.y);
          }
        };

        // Alpha: full during GROW + HOLD, then linearly fade over FADE_MS.
        const fadeStart = CASCADE_ARC_GROW_MS + CASCADE_ARC_HOLD_MS;
        const alpha = local <= fadeStart
          ? 1
          : Math.max(0, 1 - (local - fadeStart) / CASCADE_ARC_FADE_MS);

        // Three-pass bolt: wide dim halo, bright amber body, hot white core.
        // Each stroke finalizes the current path, so we rebuild it per pass.
        buildPath();
        e.gfx.stroke({ color, width: 12, alpha: alpha * 0.28 });
        buildPath();
        e.gfx.stroke({ color: 0xffcf58, width: 5, alpha });
        buildPath();
        e.gfx.stroke({ color: 0xffffff, width: 1.8, alpha });
      }
      return elapsed >= totalMs;
    },
    destroy(): void {
      if (flashGfx.parent) container.removeChild(flashGfx);
      flashGfx.destroy();
      for (const e of entries) {
        container.removeChild(e.gfx);
        e.gfx.destroy();
      }
    },
  });
}

/**
 * Powder Keg impact: a radial shockwave ring expands from the impact
 * point, and each bubble pops as the ring reaches its cell. Closer
 * cells pop first; the ring fades after the last pop.
 *
 * `overclocked` (Mk. V keg) is the dramatic detonation: a white-hot flash
 * disc, a slower triple shockwave that sweeps the whole board, a spray of
 * radial embers, and a lingering scorch ring.
 */
export function createBombImpactEffect(
  container: Container,
  impact: Vec2,
  targets: ReadonlyArray<{ position: Vec2; color: number }>,
  pool: SpritePool | null,
  reducedMotion: boolean,
  overclocked: boolean = false,
): void {
  if (reducedMotion) {
    for (const t of targets) {
      createPopSpinEffect(container, t.position, t.color, pool, reducedMotion);
    }
    return;
  }

  const SHOCKWAVE_MS = overclocked ? 520 : 220;
  const FADE_MS = overclocked ? 420 : 120;
  const FLASH_MS = 160;
  const EMBER_COUNT = overclocked ? 28 : 0;
  const EMBER_MS = 700;

  let maxDist = 0;
  const withDist = targets.map(t => {
    const dx = t.position.x - impact.x;
    const dy = t.position.y - impact.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > maxDist) maxDist = d;
    return { ...t, dist: d };
  });
  // The overclocked wave always sweeps at least half a board so the ring
  // reads as huge even when the keg lands on a sparse corner.
  const farRadius = Math.max(maxDist, overclocked ? 180 : 1);

  const popped = new Array<boolean>(withDist.length).fill(false);

  const ring = new Graphics();
  container.addChild(ring);
  const flash = overclocked ? new Graphics() : null;
  if (flash) container.addChild(flash);

  interface Ember { x: number; y: number; vx: number; vy: number; color: number; size: number }
  const embers: Ember[] = [];
  const emberGfx = overclocked ? new Graphics() : null;
  if (emberGfx) {
    container.addChild(emberGfx);
    for (let i = 0; i < EMBER_COUNT; i++) {
      const a = (i / EMBER_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
      const speed = 0.25 + Math.random() * 0.35;
      embers.push({
        x: impact.x,
        y: impact.y,
        vx: Math.cos(a) * speed,
        vy: Math.sin(a) * speed,
        color: EMBER_COLORS[i % EMBER_COLORS.length]!,
        size: 1.5 + Math.random() * 2,
      });
    }
  }

  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const total = SHOCKWAVE_MS + FADE_MS;
      if (elapsed >= total) return true;

      const expandT = Math.min(1, elapsed / SHOCKWAVE_MS);
      const currentRadius = farRadius * expandT;

      for (let i = 0; i < withDist.length; i++) {
        if (popped[i]) continue;
        if (withDist[i]!.dist <= currentRadius) {
          popped[i] = true;
          const t = withDist[i]!;
          createPopSpinEffect(container, t.position, t.color, pool, reducedMotion);
          if (overclocked) {
            createEmberParticles(container, t.position, reducedMotion);
          }
        }
      }

      ring.clear();
      if (expandT < 1) {
        const alpha = 0.55 * (1 - expandT * 0.6);
        if (overclocked) {
          // Triple wave: white-hot leading edge, brass body, crimson trail.
          ring.circle(impact.x, impact.y, currentRadius);
          ring.stroke({ color: 0xfff2c8, width: 6, alpha: alpha * 1.2 });
          ring.circle(impact.x, impact.y, currentRadius * 0.9);
          ring.stroke({ color: 0xd4a539, width: 4, alpha });
          ring.circle(impact.x, impact.y, currentRadius * 0.78);
          ring.stroke({ color: 0xc4453a, width: 3, alpha: alpha * 0.8 });
          ring.circle(impact.x, impact.y, currentRadius * 0.6);
          ring.stroke({ color: 0xff8c3a, width: 2, alpha: alpha * 0.5 });
        } else {
          ring.circle(impact.x, impact.y, currentRadius);
          ring.stroke({ color: 0xd4a539, width: 3, alpha });
          ring.circle(impact.x, impact.y, currentRadius * 0.85);
          ring.stroke({ color: 0xc4453a, width: 2, alpha: alpha * 0.7 });
        }
      } else {
        const fadeT = (elapsed - SHOCKWAVE_MS) / FADE_MS;
        const alpha = 0.35 * (1 - fadeT);
        ring.circle(impact.x, impact.y, farRadius);
        ring.stroke({ color: 0xd4a539, width: overclocked ? 4 : 2, alpha });
        if (overclocked) {
          // Lingering scorch ring around the crater.
          ring.circle(impact.x, impact.y, 26 + fadeT * 6);
          ring.stroke({ color: 0x3a1a16, width: 5, alpha: 0.6 * (1 - fadeT) });
        }
      }

      if (flash) {
        flash.clear();
        if (elapsed < FLASH_MS) {
          const t = elapsed / FLASH_MS;
          const r = 22 + t * 70;
          flash.circle(impact.x, impact.y, r);
          flash.fill({ color: 0xfff6dc, alpha: 0.85 * (1 - t) });
          flash.circle(impact.x, impact.y, r * 0.5);
          flash.fill({ color: 0xffffff, alpha: 0.95 * (1 - t) });
        }
      }

      if (emberGfx) {
        emberGfx.clear();
        if (elapsed < EMBER_MS) {
          const life = 1 - elapsed / EMBER_MS;
          for (const e of embers) {
            e.x += e.vx * dt;
            e.y += e.vy * dt;
            e.vy += GRAVITY * 0.02 * dt;
            emberGfx.circle(e.x, e.y, e.size * (0.5 + life * 0.5));
            emberGfx.fill({ color: e.color, alpha: life });
          }
        }
      }

      return false;
    },
    destroy(): void {
      for (let i = 0; i < withDist.length; i++) {
        if (!popped[i]) {
          const t = withDist[i]!;
          createPopSpinEffect(container, t.position, t.color, pool, reducedMotion);
        }
      }
      container.removeChild(ring);
      ring.destroy();
      if (flash) {
        container.removeChild(flash);
        flash.destroy();
      }
      if (emberGfx) {
        container.removeChild(emberGfx);
        emberGfx.destroy();
      }
    },
  });
}

/**
 * Create a dramatic flying-gear drop effect for disconnected bubbles.
 * Each gear launches in a random parabolic arc, spinning wildly,
 * until it flies off the bottom or side of the screen.
 */
export function createDropEffect(
  container: Container,
  positions: ReadonlyArray<Vec2>,
  color: number,
  reducedMotion: boolean,
): void {
  if (reducedMotion) return;

  const GEAR_R = 8;
  const TEETH = 8;

  interface FlyingGear {
    gfx: Graphics;
    x: number;
    y: number;
    vx: number;
    vy: number;
    spin: number;
    rot: number;
  }

  const gears: FlyingGear[] = [];

  for (const pos of positions) {
    const gfx = new Graphics();
    // Draw a proper gear shape, not a circle
    const outer = GEAR_R;
    const inner = GEAR_R * 0.75;
    const half = Math.PI / (TEETH * 2.5);
    gfx.moveTo(outer, 0);
    for (let i = 0; i < TEETH; i++) {
      const b = (i / TEETH) * Math.PI * 2;
      const n = ((i + 1) / TEETH) * Math.PI * 2;
      const a2 = b + half;
      const a4 = n - half;
      gfx.lineTo(Math.cos(a2) * outer, Math.sin(a2) * outer);
      gfx.lineTo(Math.cos(a2) * inner, Math.sin(a2) * inner);
      gfx.lineTo(Math.cos(a4) * inner, Math.sin(a4) * inner);
      gfx.lineTo(Math.cos(a4) * outer, Math.sin(a4) * outer);
    }
    gfx.closePath();
    gfx.fill({ color });
    // Axle hole
    gfx.circle(0, 0, GEAR_R * 0.2);
    gfx.fill({ color: 0x1a1510 });
    // Outline
    gfx.circle(0, 0, outer + 0.5);
    gfx.stroke({ color: 0x0a0806, width: 1 });

    gfx.position.set(pos.x, pos.y);
    container.addChild(gfx);

    // Random launch: upward + sideways, with varied trajectories
    const launchAngle = -Math.PI / 2 + (Math.random() - 0.5) * 1.2; // mostly upward, varied
    const launchSpeed = 2.5 + Math.random() * 3.5;
    gears.push({
      gfx,
      x: pos.x,
      y: pos.y,
      vx: Math.cos(launchAngle) * launchSpeed,
      vy: Math.sin(launchAngle) * launchSpeed,
      spin: (Math.random() - 0.5) * 0.4, // random spin direction + speed
      rot: Math.random() * Math.PI * 2,
    });
  }

  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      if (elapsed > DROP_MAX_MS) return true;

      let allOffscreen = true;
      for (const g of gears) {
        // Physics
        g.vy += GRAVITY * (dt / 16); // gravity (normalized to ~60fps)
        g.x += g.vx * (dt / 16);
        g.y += g.vy * (dt / 16);
        g.rot += g.spin * (dt / 16);

        g.gfx.position.set(g.x, g.y);
        g.gfx.rotation = g.rot;

        // Check if still onscreen (with generous margin)
        if (g.y < 900 && g.x > -50 && g.x < 450) {
          allOffscreen = false;
        }
      }
      return allOffscreen;
    },
    destroy(): void {
      for (const g of gears) {
        container.removeChild(g.gfx);
        g.gfx.destroy();
      }
    },
  });
}

const HEAT_ORANGE = 0xff5a1a;
const HEAT_YELLOW = 0xffd070;
const HEAT_WHITE = 0xfff4d8;
const PRISMATIC_CRACK_COUNT = 4;
const PRISMATIC_FRAGMENT_COUNT = 9;
const PRISMATIC_DEBRIS_COUNT = 10;
const PRISMATIC_FRAGMENT_MS = 800;
const PRISMATIC_FLASH_MS = 110;
const PRISMATIC_GLOW_STEPS = 10;
const PRISMATIC_RING_MS = 340;

function lerpColor(from: number, to: number, t: number): number {
  const k = Math.max(0, Math.min(1, t));
  const r = ((from >> 16) & 0xff) + (((to >> 16) & 0xff) - ((from >> 16) & 0xff)) * k;
  const g = ((from >> 8) & 0xff) + (((to >> 8) & 0xff) - ((from >> 8) & 0xff)) * k;
  const bl = (from & 0xff) + ((to & 0xff) - (from & 0xff)) * k;
  return (Math.round(r) << 16) | (Math.round(g) << 8) | Math.round(bl);
}

/** Base colour → furnace orange → yellow → white-hot as `heat` goes 0→1. */
function heatColor(base: number, heat: number): number {
  if (heat < 0.45) return lerpColor(base, HEAT_ORANGE, heat / 0.45);
  if (heat < 0.8) return lerpColor(HEAT_ORANGE, HEAT_YELLOW, (heat - 0.45) / 0.35);
  return lerpColor(HEAT_YELLOW, HEAT_WHITE, (heat - 0.8) / 0.2);
}

/**
 * Prismatic Spark — the gear at `position` overheats, rattles itself loose
 * and bursts into fragments.
 *
 * The gear starts as a board-identical held stand-in (the store has already
 * taken the real one off the displayed board), sits untouched for `delayMs`,
 * then over PRISMATIC_HEAT_MS: tints base → orange → white-hot under an
 * additive glow, vibrates with growing amplitude, spits embers, shows a
 * hue-cycling prismatic rim, and finally cracks and swells. At the end it
 * shatters — flash, shockwave ring, wedge-shaped gear fragments thrown
 * outward under gravity, plus prismatic shards. `onShatter` fires on that
 * frame (the renderer hangs the sound off it).
 */
export function createPrismaticOverheatEffect(
  container: Container,
  position: Vec2,
  color: number,
  symbol: string,
  key: string,
  delayMs: number,
  reducedMotion: boolean,
  onShatter?: () => void,
): void {
  const R = CONFIG.CELL_SIZE;

  const root = new Container();
  root.position.set(position.x, position.y);

  // Normal blend, not additive: the glow of an edge gear spills past the
  // board onto the transparent margin canvas, where 'add' renders as a
  // hard opaque disc.
  const glow = new Graphics();
  root.addChild(glow);

  const held = createHeldBoardGear(color, symbol, key);
  const gearNode: Container | Graphics = held?.node ?? (() => {
    const g = new Graphics();
    drawGearGraphic(g, R, color);
    return g;
  })();
  root.addChild(gearNode);

  // Additive copy of the gear on top — tint alone can only darken the
  // texture, this is what actually pushes the metal to white-hot.
  let hot: Sprite | null = null;
  if (held) {
    hot = new Sprite(held.sprite.texture);
    hot.anchor.set(0.5);
    hot.scale.copyFrom(held.sprite.scale);
    hot.rotation = held.sprite.rotation;
    hot.blendMode = 'add';
    hot.alpha = 0;
    root.addChild(hot);
  }

  const overlay = new Graphics(); // prismatic rim + cracks
  overlay.blendMode = 'add';
  root.addChild(overlay);

  container.addChild(root);

  // Pre-rolled jagged cracks from hub to rim, revealed as the gear fails.
  const cracks: Vec2[][] = [];
  for (let i = 0; i < PRISMATIC_CRACK_COUNT; i++) {
    const angle = (i / PRISMATIC_CRACK_COUNT) * Math.PI * 2 + Math.random() * 0.9;
    const pts: Vec2[] = [{ x: 0, y: 0 }];
    for (let j = 1; j <= 4; j++) {
      const r = (j / 4) * R * 0.95;
      const a = angle + (Math.random() - 0.5) * 0.7;
      pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r });
    }
    cracks.push(pts);
  }

  interface Ember { gfx: Graphics; vx: number; vy: number; life: number; max: number }
  const embers: Ember[] = [];
  const spawnEmber = (): void => {
    const gfx = new Graphics();
    const size = 1.2 + Math.random() * 1.6;
    gfx.rect(-size / 2, -size / 2, size, size);
    const prismatic = Math.random() < 0.35;
    const pool = prismatic ? PRISMATIC_COLORS : EMBER_COLORS;
    gfx.fill({ color: pool[Math.floor(Math.random() * pool.length)]! });
    gfx.blendMode = 'add';
    const a = Math.random() * Math.PI * 2;
    gfx.position.set(position.x + Math.cos(a) * R * 0.7, position.y + Math.sin(a) * R * 0.7);
    container.addChild(gfx);
    const speed = 0.8 + Math.random() * 1.8;
    embers.push({
      gfx,
      vx: Math.cos(a) * speed,
      vy: Math.sin(a) * speed - 1.2,
      life: 0,
      max: 260 + Math.random() * 240,
    });
  };

  const baseSpriteRotation = held ? held.sprite.rotation : 0;
  let elapsed = 0;
  let shattered = false;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const f = dt / 16.667;

      for (let i = embers.length - 1; i >= 0; i--) {
        const e = embers[i]!;
        e.life += dt;
        if (e.life >= e.max) {
          container.removeChild(e.gfx);
          e.gfx.destroy();
          embers.splice(i, 1);
          continue;
        }
        e.vy += 0.09 * f;
        e.gfx.position.x += e.vx * f;
        e.gfx.position.y += e.vy * f;
        e.gfx.alpha = 1 - e.life / e.max;
      }

      if (shattered) return embers.length === 0;

      const local = elapsed - delayMs;
      if (local < 0) return false; // held — pixel-identical to the board gear

      const h = local / PRISMATIC_HEAT_MS;
      if (h >= 1) {
        shattered = true;
        root.visible = false;
        spawnPrismaticShatter(container, position, color, reducedMotion);
        onShatter?.();
        return embers.length === 0;
      }

      const heat = Math.pow(h, 1.5);
      const tint = heatColor(color, heat);
      if (held) held.sprite.tint = tint;
      else drawGearGraphic(gearNode as Graphics, R, tint);
      if (hot) {
        hot.tint = lerpColor(HEAT_ORANGE, 0xffffff, heat);
        hot.alpha = heat * 0.7;
      }

      if (reducedMotion) return false;

      // Rattle: per-frame random offset, amplitude ramping with heat.
      const amp = 0.4 + 3.6 * heat;
      root.position.set(
        position.x + (Math.random() - 0.5) * 2 * amp,
        position.y + (Math.random() - 0.5) * 2 * amp,
      );
      const twitch = (Math.random() - 0.5) * 0.3 * heat;
      if (held) held.sprite.rotation = baseSpriteRotation + twitch;
      if (hot) hot.rotation = baseSpriteRotation + twitch;
      // Pressure swell in the last stretch before it lets go.
      const swellT = Math.max(0, (h - 0.7) / 0.3);
      root.scale.set(1 + 0.16 * swellT * swellT);

      const flicker = 0.85 + Math.random() * 0.3;
      const glowColor = lerpColor(HEAT_ORANGE, HEAT_YELLOW, heat);
      const glowR = R * (1.2 + 0.7 * heat) * flicker;
      glow.clear();
      // Stacked thin layers ≈ a radial falloff; no visible disc edges.
      for (let k = 0; k < PRISMATIC_GLOW_STEPS; k++) {
        const u = 1 - k / PRISMATIC_GLOW_STEPS;
        glow.circle(0, 0, glowR * u);
        glow.fill({ color: glowColor, alpha: 0.03 + 0.06 * heat });
      }

      overlay.clear();
      const rimColor = PRISMATIC_COLORS[Math.floor(local / 70) % PRISMATIC_COLORS.length]!;
      overlay.circle(0, 0, R * 1.08);
      overlay.stroke({ color: rimColor, width: 1.5, alpha: 0.55 * Math.sin(Math.min(1, h / 0.8) * Math.PI) });
      const crackT = Math.max(0, (h - 0.4) / 0.5);
      if (crackT > 0) {
        for (const pts of cracks) {
          const reach = crackT * (pts.length - 1);
          overlay.moveTo(pts[0]!.x, pts[0]!.y);
          for (let j = 1; j < pts.length; j++) {
            if (j <= reach) {
              overlay.lineTo(pts[j]!.x, pts[j]!.y);
            } else {
              const part = reach - (j - 1);
              if (part > 0) {
                const p0 = pts[j - 1]!;
                const p1 = pts[j]!;
                overlay.lineTo(p0.x + (p1.x - p0.x) * part, p0.y + (p1.y - p0.y) * part);
              }
              break;
            }
          }
          overlay.stroke({ color: 0xffffff, width: 2, alpha: 0.95 });
        }
      }

      if (Math.random() < (0.08 + 0.6 * heat) * f) spawnEmber();
      return false;
    },
    destroy(): void {
      for (const e of embers) {
        if (e.gfx.parent) container.removeChild(e.gfx);
        e.gfx.destroy();
      }
      if (root.parent) container.removeChild(root);
      root.destroy({ children: true });
    },
  });
}

/**
 * The burst at the end of the overheat: white flash + expanding ring, the
 * gear itself breaking into wedge fragments that fly out white-hot, cool to
 * the gear's colour and fall away, fine debris, and prismatic shards.
 */
function spawnPrismaticShatter(
  container: Container,
  position: Vec2,
  color: number,
  reducedMotion: boolean,
): void {
  spawnPrismaticShards(container, position);
  if (reducedMotion) return;

  const R = CONFIG.CELL_SIZE;

  const flash = new Graphics();
  flash.position.set(position.x, position.y);
  container.addChild(flash);

  interface Fragment {
    gfx: Graphics; vx: number; vy: number; spin: number; max: number; draw: (c: number) => void;
  }
  const fragments: Fragment[] = [];

  // Wedges: slice the gear like a pie, each slice keeps its bit of toothed rim.
  const wedge = (Math.PI * 2) / PRISMATIC_FRAGMENT_COUNT;
  const startAngle = Math.random() * Math.PI * 2;
  for (let i = 0; i < PRISMATIC_FRAGMENT_COUNT; i++) {
    const a0 = startAngle + i * wedge;
    const a1 = a0 + wedge * (0.8 + Math.random() * 0.2);
    const mid = (a0 + a1) / 2;
    const innerR = R * (0.15 + Math.random() * 0.2);
    const cx = Math.cos(mid) * R * 0.6;
    const cy = Math.sin(mid) * R * 0.6;
    const pts = [
      { x: Math.cos(a0) * innerR - cx, y: Math.sin(a0) * innerR - cy },
      { x: Math.cos(a0) * R * 0.78 - cx, y: Math.sin(a0) * R * 0.78 - cy },
      { x: Math.cos(a0 + wedge * 0.25) * R - cx, y: Math.sin(a0 + wedge * 0.25) * R - cy },
      { x: Math.cos(a1 - wedge * 0.25) * R - cx, y: Math.sin(a1 - wedge * 0.25) * R - cy },
      { x: Math.cos(a1) * R * 0.78 - cx, y: Math.sin(a1) * R * 0.78 - cy },
      { x: Math.cos(a1) * innerR - cx, y: Math.sin(a1) * innerR - cy },
    ];
    const gfx = new Graphics();
    const draw = (c: number): void => {
      gfx.clear();
      gfx.moveTo(pts[0]!.x, pts[0]!.y);
      for (let j = 1; j < pts.length; j++) gfx.lineTo(pts[j]!.x, pts[j]!.y);
      gfx.closePath();
      gfx.fill({ color: c });
    };
    draw(HEAT_YELLOW);
    gfx.position.set(position.x + cx * 1.5, position.y + cy * 1.5);
    gfx.rotation = (Math.random() - 0.5) * 0.8;
    container.addChild(gfx);
    const speed = 2.6 + Math.random() * 3.2;
    fragments.push({
      gfx,
      vx: Math.cos(mid) * speed,
      vy: Math.sin(mid) * speed - 1.6,
      spin: (Math.random() - 0.5) * 0.5,
      max: PRISMATIC_FRAGMENT_MS * (0.75 + Math.random() * 0.25),
      draw,
    });
  }

  for (let i = 0; i < PRISMATIC_DEBRIS_COUNT; i++) {
    const gfx = new Graphics();
    const size = 1.5 + Math.random() * 2;
    const draw = (c: number): void => {
      gfx.clear();
      gfx.rect(-size / 2, -size / 2, size, size);
      gfx.fill({ color: c });
    };
    draw(HEAT_WHITE);
    gfx.position.set(position.x, position.y);
    container.addChild(gfx);
    const a = Math.random() * Math.PI * 2;
    const speed = 4 + Math.random() * 4.5;
    fragments.push({
      gfx,
      vx: Math.cos(a) * speed,
      vy: Math.sin(a) * speed - 1.2,
      spin: (Math.random() - 0.5) * 0.8,
      max: PRISMATIC_FRAGMENT_MS * (0.4 + Math.random() * 0.35),
      draw,
    });
  }

  const cooled = lerpColor(color, 0x1a1510, 0.45);
  let elapsed = 0;

  // Effects pushed mid-tick aren't updated until next frame — paint the
  // flash now so the burst's first frame isn't bare fragments.
  flash.circle(0, 0, R * 1.1);
  flash.fill({ color: HEAT_WHITE, alpha: 0.85 });

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const f = dt / 16.667;

      flash.clear();
      if (elapsed < PRISMATIC_FLASH_MS) {
        const t = elapsed / PRISMATIC_FLASH_MS;
        flash.circle(0, 0, R * (1.1 + 0.9 * t));
        flash.fill({ color: HEAT_WHITE, alpha: 0.85 * (1 - t) * (1 - t) });
      }
      if (elapsed < PRISMATIC_RING_MS) {
        const t = elapsed / PRISMATIC_RING_MS;
        flash.circle(0, 0, R * (1 + 2.4 * t));
        flash.stroke({ color: HEAT_YELLOW, width: 3 * (1 - t) + 0.5, alpha: 0.8 * (1 - t) });
      }

      let alive = false;
      for (const fr of fragments) {
        const t = elapsed / fr.max;
        if (t >= 1) {
          fr.gfx.visible = false;
          continue;
        }
        alive = true;
        fr.vy += 0.3 * f;
        fr.vx *= Math.pow(0.985, f);
        fr.gfx.position.x += fr.vx * f;
        fr.gfx.position.y += fr.vy * f;
        fr.gfx.rotation += fr.spin * f;
        // White-hot → orange → the gear's own colour → sooty as it cools.
        const c = t < 0.2
          ? lerpColor(HEAT_WHITE, HEAT_ORANGE, t / 0.2)
          : t < 0.5
            ? lerpColor(HEAT_ORANGE, color, (t - 0.2) / 0.3)
            : lerpColor(color, cooled, (t - 0.5) / 0.5);
        fr.draw(c);
        fr.gfx.alpha = t > 0.65 ? 1 - (t - 0.65) / 0.35 : 1;
      }
      return !alive && elapsed >= PRISMATIC_RING_MS;
    },
    destroy(): void {
      if (flash.parent) container.removeChild(flash);
      flash.destroy();
      for (const fr of fragments) {
        if (fr.gfx.parent) container.removeChild(fr.gfx);
        fr.gfx.destroy();
      }
    },
  });
}

function spawnPrismaticShards(container: Container, position: Vec2): void {
  const shards: { gfx: Graphics; vx: number; vy: number; rot: number; spin: number }[] = [];

  for (let i = 0; i < PRISMATIC_SHARD_COUNT; i++) {
    const angle = (i / PRISMATIC_SHARD_COUNT) * Math.PI * 2 + Math.random() * 0.3;
    const speed = 1.5 + Math.random() * 1.5;
    const color = PRISMATIC_COLORS[i % PRISMATIC_COLORS.length]!;

    const gfx = new Graphics();
    // Diamond-shaped shard
    const size = 3 + Math.random() * 2;
    gfx.moveTo(0, -size);
    gfx.lineTo(size * 0.5, 0);
    gfx.lineTo(0, size);
    gfx.lineTo(-size * 0.5, 0);
    gfx.closePath();
    gfx.fill({ color });
    gfx.position.set(position.x, position.y);
    container.addChild(gfx);

    shards.push({
      gfx,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      rot: Math.random() * Math.PI * 2,
      spin: (Math.random() - 0.5) * 0.3,
    });
  }

  let elapsed = 0;
  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / PRISMATIC_SHARD_MS;
      if (t >= 1) return true;
      for (const s of shards) {
        s.gfx.position.x += s.vx;
        s.gfx.position.y += s.vy;
        s.rot += s.spin;
        s.gfx.rotation = s.rot;
        s.gfx.alpha = 1 - t;
        s.gfx.scale.set(1 - t * 0.3);
      }
      return false;
    },
    destroy(): void {
      for (const s of shards) {
        container.removeChild(s.gfx);
        s.gfx.destroy();
      }
    },
  });
}

/**
 * Create animated score text that flies upward — brass/gold mechanical counter style.
 */
export function createScoreFlyUp(
  container: Container,
  position: Vec2,
  breakdown: ReadonlyArray<ScoreBreakdown>,
  reducedMotion: boolean,
  frenzyActive: boolean = false,
): void {
  if (breakdown.length === 0) return;

  const texts: Text[] = [];

  const baseStyle = new TextStyle({
    fontFamily: '"Courier New", monospace',
    fontSize: 14,
    fill: 0xc49a3c, // brass gold
    fontWeight: 'bold',
    stroke: { color: 0x1a1510, width: 2 },
  });
  const frenzyBaseStyle = new TextStyle({
    fontFamily: '"Courier New", monospace',
    fontSize: Math.round(14 * 1.3),
    fill: FRENZY_AMBER,
    fontWeight: 'bold',
    stroke: { color: 0x1a1510, width: 2 },
  });

  function isBasePointsLabel(label: string): boolean {
    return label === 'Clusters popped' || label === 'Bubbles dropped';
  }

  let yOffset = 0;
  for (const item of breakdown) {
    if (item.points === 0) continue;

    const label = `${item.label}: +${item.points}`;
    const useFrenzy = frenzyActive && isBasePointsLabel(item.label);
    const text = new Text({ text: label, style: useFrenzy ? frenzyBaseStyle : baseStyle });
    text.anchor.set(0.5);
    text.position.set(position.x, position.y - yOffset);
    container.addChild(text);
    texts.push(text);
    yOffset += useFrenzy ? 22 : 18;
  }

  if (texts.length === 0) return;

  if (reducedMotion) {
    const timer = setTimeout(() => {
      for (const text of texts) {
        container.removeChild(text);
        text.destroy();
      }
    }, 1500);

    activeEffects.push({
      update(): boolean { return false; },
      destroy(): void {
        clearTimeout(timer);
        for (const text of texts) {
          if (text.parent) container.removeChild(text);
          text.destroy();
        }
      },
    });
    return;
  }

  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / FLY_UP_DURATION_MS;
      if (t >= 1) return true;

      for (const text of texts) {
        text.position.y -= (FLY_UP_DISTANCE * dt) / FLY_UP_DURATION_MS;
        text.alpha = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
      }
      return false;
    },
    destroy(): void {
      for (const text of texts) {
        container.removeChild(text);
        text.destroy();
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Personal Best flash — steampunk brass fanfare
// ---------------------------------------------------------------------------

const PB_DURATION_MS = 2000;
const PB_CONFETTI_COUNT = 24;

/**
 * Create a "NEW BEST!" effect with brass/copper gear confetti.
 * Auto-dismisses after 2 seconds.
 */
export function createPersonalBestFlash(
  container: Container,
  currentScore: number,
  previousBest: number,
  reducedMotion: boolean = false,
): void {
  const mainStyle = new TextStyle({
    fontFamily: '"Courier New", monospace',
    fontSize: 28,
    fill: 0xc49a3c, // brass gold
    fontWeight: 'bold',
    stroke: { color: 0x1a1510, width: 3 },
    align: 'center',
  });

  const subStyle = new TextStyle({
    fontFamily: '"Courier New", monospace',
    fontSize: 14,
    fill: 0xc49a3c,
    stroke: { color: 0x1a1510, width: 2 },
    align: 'center',
  });

  const mainText = new Text({
    text: `NEW BEST! ${currentScore.toLocaleString()}`,
    style: mainStyle,
  });
  mainText.anchor.set(0.5);
  mainText.position.set(375 / 2, 667 / 3);
  container.addChild(mainText);

  const subText = new Text({
    text: `(was ${previousBest.toLocaleString()})`,
    style: subStyle,
  });
  subText.anchor.set(0.5);
  subText.position.set(375 / 2, 667 / 3 + 32);
  container.addChild(subText);

  // Gear-tooth confetti in steampunk metals
  const confettiColors = [0xc49a3c, 0xc4453a, 0xd4a539, 0x8b6aad, 0xc07830, 0x4a8fa8];
  const confetti: { gfx: Graphics; vx: number; vy: number }[] = [];

  if (!reducedMotion) {
    for (let i = 0; i < PB_CONFETTI_COUNT; i++) {
      const angle = (i / PB_CONFETTI_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
      const speed = 2 + Math.random() * 3;
      const gfx = new Graphics();
      const size = 2 + Math.random() * 3;
      // Mix of rectangles (gear teeth) and small circles (rivets)
      if (i % 3 === 0) {
        gfx.circle(0, 0, size / 2);
      } else {
        gfx.rect(-size / 2, -size / 2, size, size * 1.5);
      }
      gfx.fill({ color: confettiColors[i % confettiColors.length]! });
      gfx.position.set(375 / 2, 667 / 3);
      container.addChild(gfx);

      confetti.push({
        gfx,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
      });
    }
  }

  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / PB_DURATION_MS;
      if (t >= 1) return true;

      const alpha = t < 0.7 ? 1 : 1 - (t - 0.7) / 0.3;
      mainText.alpha = alpha;
      subText.alpha = alpha;

      if (t < 0.15) {
        const scale = 1 + (1 - t / 0.15) * 0.3;
        mainText.scale.set(scale);
      } else {
        mainText.scale.set(1);
      }

      for (const c of confetti) {
        c.vy += 0.08;
        c.gfx.position.x += c.vx;
        c.gfx.position.y += c.vy;
        c.gfx.alpha = alpha;
        c.gfx.rotation += 0.1;
      }

      return false;
    },
    destroy(): void {
      container.removeChild(mainText);
      mainText.destroy();
      container.removeChild(subText);
      subText.destroy();
      for (const c of confetti) {
        container.removeChild(c.gfx);
        c.gfx.destroy();
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Oil trail — amber droplets emitted at wall bounces while Lubricated Bearings
// is active. 200ms total lifetime, 3-4 droplets.
// ---------------------------------------------------------------------------

const OIL_TRAIL_MS = 200;
const OIL_DROPLET_COUNT = 4;

export function createOilTrailEffect(
  container: Container,
  position: Vec2,
  reducedMotion: boolean,
): void {
  if (reducedMotion) return;

  const droplets: { gfx: Graphics; vx: number; vy: number }[] = [];

  for (let i = 0; i < OIL_DROPLET_COUNT; i++) {
    const angle = (i / OIL_DROPLET_COUNT) * Math.PI * 2 + Math.random() * 0.5;
    const speed = 0.6 + Math.random() * 0.8;
    const gfx = new Graphics();
    const r = 1.8 + Math.random() * 1.2;
    gfx.circle(0, 0, r);
    gfx.fill({ color: 0xc49a3c, alpha: 0.85 });
    gfx.position.set(position.x, position.y);
    container.addChild(gfx);
    droplets.push({
      gfx,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
    });
  }

  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / OIL_TRAIL_MS;
      if (t >= 1) return true;
      for (const d of droplets) {
        d.vy += 0.08 * (dt / 16);
        d.gfx.position.x += d.vx * (dt / 16);
        d.gfx.position.y += d.vy * (dt / 16);
        d.gfx.alpha = 0.85 * (1 - t);
        d.gfx.scale.set(1 - t * 0.4);
      }
      return false;
    },
    destroy(): void {
      for (const d of droplets) {
        container.removeChild(d.gfx);
        d.gfx.destroy();
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Bank-shot tick — a quiet cue at every wall bounce that banking pays.
// A short brass glint on the wall plus a small "Bank ×N" tag that drifts
// inward and fades. N is the running bank factor on the shot's base points
// (1 + coef × bounces, matching calculateScore), so a second bounce visibly
// climbs from ×2.5 to ×4. Kept small and brief (~700ms) so it reads as a
// ticker, not a celebration.
// ---------------------------------------------------------------------------

const BANK_TICK_MS = 700;
const BANK_GLINT_MS = 260;
const BANK_TICK_DRIFT = 16;
const BANK_TICK_INSET = 14;

const BANK_TICK_STYLE = new TextStyle({
  fontFamily: '"Courier New", monospace',
  fontSize: 12,
  fill: 0xc49a3c, // brass gold
  fontWeight: 'bold',
  stroke: { color: 0x1a1510, width: 2 },
});

/** Format the cumulative bank factor for `bounces` wall hits: 1 → "2.5", 2 → "4". */
export function bankFactorLabel(bounces: number, bankCoefX10: number): string {
  const totalX10 = 10 + bankCoefX10 * bounces;
  return totalX10 % 10 === 0 ? String(totalX10 / 10) : (totalX10 / 10).toFixed(1);
}

export function createBankShotEffect(
  container: Container,
  position: Vec2,
  /** Which wall was hit — the tag drifts away from it, toward the board. */
  side: 'left' | 'right',
  bounces: number,
  bankCoefX10: number,
  reducedMotion: boolean,
): void {
  const inward = side === 'left' ? 1 : -1;

  const text = new Text({
    text: `Bank ×${bankFactorLabel(bounces, bankCoefX10)}`,
    style: BANK_TICK_STYLE,
  });
  text.anchor.set(side === 'left' ? 0 : 1, 0.5);
  text.position.set(position.x + inward * BANK_TICK_INSET, position.y);
  container.addChild(text);

  // Glint: a thin brass arc hugging the wall at the contact point.
  const glint = new Graphics();
  glint.position.set(position.x, position.y);
  container.addChild(glint);
  const drawGlint = (r: number, alpha: number): void => {
    glint.clear();
    const start = side === 'left' ? -Math.PI / 2 : Math.PI / 2;
    glint.arc(0, 0, r, start, start + Math.PI);
    glint.stroke({ color: 0xe8c26a, width: 1.5, alpha });
  };
  drawGlint(5, 0.9);

  const startY = text.position.y;
  const startX = text.position.x;
  let elapsed = 0;

  activeEffects.push({
    update(dt: number): boolean {
      elapsed += dt;
      const t = elapsed / BANK_TICK_MS;
      if (t >= 1) return true;

      const g = Math.min(1, elapsed / BANK_GLINT_MS);
      if (g < 1) drawGlint(reducedMotion ? 5 : 5 + g * 7, 0.9 * (1 - g));
      else glint.visible = false;

      if (!reducedMotion) {
        // Quick settle-in (overshoot-free) then a slow upward drift.
        const pop = Math.min(1, elapsed / 90);
        text.scale.set(0.75 + 0.25 * pop);
        text.position.set(startX + inward * 4 * t, startY - BANK_TICK_DRIFT * t);
      }
      text.alpha = t < 0.55 ? 1 : 1 - (t - 0.55) / 0.45;
      return false;
    },
    destroy(): void {
      container.removeChild(text);
      text.destroy();
      container.removeChild(glint);
      glint.destroy();
    },
  });
}
